import React, { useState, useEffect, useCallback } from "react";
import { MainLayout } from "../components/Layout";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Settings, CheckCircle, XCircle, AlertTriangle, Loader2, Key, 
  Webhook, Zap, RefreshCw, ExternalLink, Copy, Store, ShoppingBag,
  Clock, Package, Users, Menu, PlayCircle, PauseCircle, UtensilsCrossed,
  Database, Upload, Image, Check, X, ArrowRight
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
import { KeetaHomologationTab } from "../components/keeta/KeetaHomologationTab";


// Mapa de dias da semana em ingles -> portugues
const DAY_MAP = {
  MONDAY: "Seg",
  TUESDAY: "Ter",
  WEDNESDAY: "Qua",
  THURSDAY: "Qui",
  FRIDAY: "Sex",
  SATURDAY: "Sab",
  SUNDAY: "Dom"
};

const DAY_ORDER = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];

function utcToBrt(timeStr) {
  if (!timeStr) return "";
  const parts = timeStr.replace(".000Z", "").split(":");
  let h = parseInt(parts[0], 10) - 3;
  if (h < 0) h += 24;
  return String(h).padStart(2, "0") + ":" + parts[1];
}

function KeetaStoreHours({ hours }) {
  if (!hours || hours.length === 0) return <p className="text-sm text-muted-foreground">Sem horarios configurados</p>;

  const dayPeriods = {};
  DAY_ORDER.forEach(d => { dayPeriods[d] = []; });

  hours.forEach(entry => {
    const days = entry.dayOfWeek || [];
    const tp = entry.timePeriods;
    if (!tp) return;
    const start = utcToBrt(tp.startTime);
    const end = utcToBrt(tp.endTime);
    if (start === end) return;
    days.forEach(day => {
      if (dayPeriods[day]) {
        dayPeriods[day].push({ start, end });
      }
    });
  });

  DAY_ORDER.forEach(d => {
    dayPeriods[d].sort((a, b) => a.start.localeCompare(b.start));
    const merged = [];
    dayPeriods[d].forEach(p => {
      if (merged.length > 0) {
        const last = merged[merged.length - 1];
        if (p.start <= last.end || (last.end >= "21:00" && p.start <= "00:00")) {
          last.end = p.end > last.end ? p.end : last.end;
          return;
        }
      }
      merged.push({ ...p });
    });
    dayPeriods[d] = merged;
  });

  return (
    <div className="grid grid-cols-7 gap-2">
      {DAY_ORDER.map(day => {
        const periods = dayPeriods[day];
        const isClosed = periods.length === 0;
        const totalHours = periods.reduce((acc, p) => {
          const [sh, sm] = p.start.split(":").map(Number);
          let [eh, em] = p.end.split(":").map(Number);
          if (eh < sh) eh += 24;
          return acc + (eh - sh) + (em - sm) / 60;
        }, 0);
        
        return (
          <div key={day} className={isClosed ? "rounded-lg border p-3 text-center bg-slate-100 opacity-60" : "rounded-lg border p-3 text-center bg-white"}>
            <p className="font-semibold text-sm">{DAY_MAP[day]}</p>
            <p className="text-xs text-muted-foreground mb-2">
              {isClosed ? "Fechado" : totalHours.toFixed(1) + "h"}
            </p>
            {isClosed ? (
              <div className="py-4">
                <p className="text-xs text-muted-foreground">--</p>
              </div>
            ) : (
              <div className="space-y-1">
                {periods.map((p, idx) => (
                  <div key={idx} className="bg-green-50 border border-green-200 rounded px-2 py-1">
                    <p className="text-xs font-medium text-green-800">{p.start}-{p.end}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function KeetaPage() {
  const [activeTab, setActiveTab] = useState("config");
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [settingToken, setSettingToken] = useState(false);
  const [refreshingToken, setRefreshingToken] = useState(false);
  
  // Config states
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [appId, setAppId] = useState("");
  const [merchantId, setMerchantId] = useState("");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [manualToken, setManualToken] = useState("");
  const [manualRefreshToken, setManualRefreshToken] = useState("");
  
  // Orders states
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  
  // Store states
  const [storeInfo, setStoreInfo] = useState(null);
  const [loadingStore, setLoadingStore] = useState(false);
  const [togglingStore, setTogglingStore] = useState(false);
  
  // Menu states
  const [localMenu, setLocalMenu] = useState({ categories: [], products: [] });
  const [loadingMenu, setLoadingMenu] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncHistory, setSyncHistory] = useState([]);
  
  // Logs states
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logFilter, setLogFilter] = useState("ALL");
  const [selectedLog, setSelectedLog] = useState(null);
  
  // Status
  const [integrationStatus, setIntegrationStatus] = useState(null);

  const getAuthHeaders = () => ({
    "Authorization": `Bearer ${localStorage.getItem("token")}`,
    "Content-Type": "application/json"
  });

  const loadConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/keeta/config`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
        setClientId(data.client_id || "");
        setAppId(data.app_id || "");
        setMerchantId(data.merchant_id || "");
        setWebhookUrl(data.webhook_url || "");
        setEnabled(data.enabled || false);
      }
    } catch (err) {
      console.error("Erro ao carregar config:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleRefreshToken = async () => {
    setRefreshingToken(true);
    try {
      const res = await fetch(`${API_URL}/api/keeta/token/force-refresh`, {
        method: "POST",
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message || "Token renovado com sucesso!");
        loadConfig();
      } else {
        toast.error(data.message || "Erro ao renovar token");
      }
    } catch (err) {
      toast.error("Erro ao renovar token: " + err.message);
    } finally {
      setRefreshingToken(false);
    }
  };

  const loadOrders = useCallback(async () => {
    setLoadingOrders(true);
    try {
      const res = await fetch(`${API_URL}/api/keeta/orders?limit=50`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setOrders(data.orders || []);
      }
    } catch (err) {
      console.error("Erro ao carregar pedidos:", err);
    } finally {
      setLoadingOrders(false);
    }
  }, []);

  const loadStoreInfo = useCallback(async () => {
    setLoadingStore(true);
    try {
      const res = await fetch(`${API_URL}/api/keeta/store`, { headers: getAuthHeaders() });
      if (res.ok) {
        setStoreInfo(await res.json());
      }
    } catch (err) {
      console.error("Erro ao carregar loja:", err);
    } finally {
      setLoadingStore(false);
    }
  }, []);

  const loadLocalMenu = useCallback(async () => {
    setLoadingMenu(true);
    try {
      const res = await fetch(`${API_URL}/api/keeta/menu/local`, { headers: getAuthHeaders() });
      if (res.ok) {
        setLocalMenu(await res.json());
      }
    } catch (err) {
      console.error("Erro ao carregar menu:", err);
    } finally {
      setLoadingMenu(false);
    }
  }, []);

  const loadSyncHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/keeta/menu/syncs?limit=10`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setSyncHistory(data.syncs || []);
      }
    } catch (err) {
      console.error("Erro ao carregar histórico:", err);
    }
  }, []);

  const loadLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const filter = logFilter !== "ALL" ? `&log_type=${logFilter}` : "";
      const res = await fetch(`${API_URL}/api/keeta/logs?limit=50${filter}`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs || []);
      }
    } catch (err) {
      console.error("Erro ao carregar logs:", err);
      toast.error("Erro ao carregar logs");
    } finally {
      setLogsLoading(false);
    }
  }, [logFilter]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    if (activeTab === "orders") loadOrders();
    if (activeTab === "store") loadStoreInfo();
    if (activeTab === "menu") { loadLocalMenu(); loadSyncHistory(); }
    if (activeTab === "logs") loadLogs();
  }, [activeTab, loadOrders, loadStoreInfo, loadLocalMenu, loadSyncHistory, loadLogs]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        client_id: clientId || null,
        app_id: appId || null,
        merchant_id: merchantId || null,
        enabled: enabled
      };
      if (clientSecret) payload.client_secret = clientSecret;
      
      const res = await fetch(`${API_URL}/api/keeta/config`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        toast.success("Configuração salva!");
        setClientSecret("");
        loadConfig();
      } else {
        const data = await res.json();
        toast.error(data.detail || "Erro ao salvar");
      }
    } catch (err) {
      toast.error("Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const handleSetManualToken = async () => {
    if (!manualToken.trim()) {
      toast.error("Informe o access token");
      return;
    }
    setSettingToken(true);
    try {
      const res = await fetch(`${API_URL}/api/keeta/token/manual`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          access_token: manualToken.trim(),
          refresh_token: manualRefreshToken.trim() || null,
          expires_in_days: 90
        })
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Token configurado!");
        setManualToken("");
        setManualRefreshToken("");
        loadConfig();
      } else {
        toast.error(data.message || "Erro");
      }
    } catch (err) {
      toast.error("Erro ao configurar token");
    } finally {
      setSettingToken(false);
    }
  };

  const handleOrderAction = async (orderId, action) => {
    try {
      const res = await fetch(`${API_URL}/api/keeta/orders/${orderId}/${action}`, {
        method: "POST",
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message);
        loadOrders();
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      toast.error("Erro na ação");
    }
  };

  const handleStoreToggle = async (action) => {
    setTogglingStore(true);
    try {
      const res = await fetch(`${API_URL}/api/keeta/store/${action}`, {
        method: "POST",
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message);
        loadStoreInfo();
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      toast.error("Erro ao alterar status");
    } finally {
      setTogglingStore(false);
    }
  };

  const handleSyncMenu = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API_URL}/api/keeta/menu/sync`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ sync_type: "FULL" })
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Menu sincronizado com sucesso!");
        loadSyncHistory();
        loadLogs();
      } else {
        toast.error(data.message || "Erro na sincronização");
      }
    } catch (err) {
      toast.error("Erro ao sincronizar menu");
    } finally {
      setSyncing(false);
    }
  };

  const copyWebhookUrl = () => {
    navigator.clipboard.writeText(`${window.location.origin}/api/keeta/webhook/v1/newEvent`);
    toast.success("URL copiada!");
  };

  const getStatusBadge = (code) => {
    if (code >= 200 && code < 300) return <Badge className="bg-green-100 text-green-800">{code}</Badge>;
    if (code >= 400 && code < 500) return <Badge className="bg-amber-100 text-amber-800">{code}</Badge>;
    if (code >= 500) return <Badge variant="destructive">{code}</Badge>;
    return <Badge variant="secondary">{code || "?"}</Badge>;
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </MainLayout>
    );
  }

  const hasValidToken = config?.has_access_token;
  const daysRemaining = config?.token_expires_at 
    ? Math.floor((new Date(config.token_expires_at) - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Settings className="w-6 h-6" />
              Integração Keeta
            </h1>
            <p className="text-muted-foreground">Gerencie pedidos e configurações da Keeta</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefreshToken}
              disabled={refreshingToken}
              className={hasValidToken && daysRemaining > 0 ? "bg-green-100 text-green-800 border-green-300 hover:bg-green-200" : "bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200"}
            >
              {refreshingToken ? (
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <RefreshCw className="w-3 h-3 mr-1" />
              )}
              {hasValidToken && daysRemaining > 0 ? `Token Válido (${daysRemaining}d)` : "Renovar Token"}
            </Button>
            <Badge variant={config?.enabled ? "default" : "secondary"}>
              {config?.enabled ? "Ativa" : "Desativada"}
            </Badge>
          </div>
        </div>

        {/* Status Alert */}
        {!hasValidToken && (
          <Alert className="bg-amber-50 border-amber-200">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              <strong>Token não configurado.</strong> Obtenha no Portal Keeta.
            </AlertDescription>
          </Alert>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="config" className="flex items-center gap-2">
              <Key className="w-4 h-4" /><span className="hidden sm:inline">Configuração</span>
            </TabsTrigger>
            <TabsTrigger value="orders" className="flex items-center gap-2">
              <ShoppingBag className="w-4 h-4" /><span className="hidden sm:inline">Pedidos</span>
              {orders.length > 0 && <Badge variant="secondary" className="ml-1">{orders.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="store" className="flex items-center gap-2">
              <Store className="w-4 h-4" /><span className="hidden sm:inline">Loja</span>
            </TabsTrigger>
            <TabsTrigger value="menu" className="flex items-center gap-2">
              <UtensilsCrossed className="w-4 h-4" /><span className="hidden sm:inline">Menu</span>
            </TabsTrigger>
            <TabsTrigger value="homologacao" className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" /><span className="hidden sm:inline">Homologação</span>
            </TabsTrigger>
            <TabsTrigger value="logs" className="flex items-center gap-2" onClick={loadLogs}>
              <Database className="w-4 h-4" /><span className="hidden sm:inline">Logs</span>
            </TabsTrigger>
          </TabsList>

          {/* TAB: Configuração */}
          <TabsContent value="config" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Token Manual */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="w-5 h-5" />
                    Configurar Token (Simplified Mode)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Access Token *</Label>
                      <Textarea value={manualToken} onChange={(e) => setManualToken(e.target.value)} placeholder="Cole o access token..." rows={2} />
                    </div>
                    <div className="space-y-2">
                      <Label>Refresh Token (opcional)</Label>
                      <Textarea value={manualRefreshToken} onChange={(e) => setManualRefreshToken(e.target.value)} placeholder="Cole o refresh token..." rows={2} />
                    </div>
                  </div>
                  <Button onClick={handleSetManualToken} disabled={settingToken || !manualToken.trim()}>
                    {settingToken && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Salvar Token
                  </Button>
                </CardContent>
              </Card>

              {/* Credenciais */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><Key className="w-5 h-5" />Credenciais</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Client ID</Label>
                    <Input value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="Client ID" />
                  </div>
                  <div className="space-y-2">
                    <Label>Client Secret</Label>
                    <Input type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} placeholder="••••••••" />
                  </div>
                  <div className="space-y-2">
                    <Label>App ID</Label>
                    <Input value={appId} onChange={(e) => setAppId(e.target.value)} placeholder="App ID" />
                  </div>
                  <div className="space-y-2">
                    <Label>Merchant ID</Label>
                    <Input value={merchantId} onChange={(e) => setMerchantId(e.target.value)} placeholder="Merchant ID" />
                  </div>
                </CardContent>
              </Card>

              {/* Webhook e Ativação */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><Webhook className="w-5 h-5" />Webhook & Ativação</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>URL do Webhook</Label>
                    <div className="flex gap-2">
                      <Input value={`${window.location.origin}/api/keeta/webhook/v1/newEvent`} readOnly className="font-mono text-xs" />
                      <Button variant="outline" size="icon" onClick={copyWebhookUrl}><Copy className="w-4 h-4" /></Button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      {enabled ? <CheckCircle className="w-6 h-6 text-green-600" /> : <XCircle className="w-6 h-6 text-slate-400" />}
                      <div>
                        <p className="font-medium">Integração</p>
                        <p className="text-sm text-muted-foreground">{enabled ? "Ativa" : "Desativada"}</p>
                      </div>
                    </div>
                    <Switch checked={enabled} onCheckedChange={setEnabled} />
                  </div>
                  <Button onClick={handleSave} disabled={saving} className="w-full">
                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Salvar Configuração
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* TAB: Pedidos */}
          <TabsContent value="orders" className="space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Pedidos Keeta</CardTitle>
                  <CardDescription>Pedidos recebidos da plataforma Keeta</CardDescription>
                </div>
                <Button onClick={loadOrders} variant="outline" size="sm" disabled={loadingOrders}>
                  <RefreshCw className={`w-4 h-4 ${loadingOrders ? "animate-spin" : ""}`} />
                </Button>
              </CardHeader>
              <CardContent>
                {loadingOrders ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin" /></div>
                ) : orders.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">Nenhum pedido encontrado</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-2">ID</th>
                          <th className="text-left p-2">Display</th>
                          <th className="text-left p-2">Cliente</th>
                          <th className="text-left p-2">Total</th>
                          <th className="text-left p-2">Status</th>
                          <th className="text-left p-2">Local</th>
                          <th className="text-left p-2">Criado</th>
                          <th className="text-left p-2">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map(order => (
                          <tr key={order.id} className="border-b hover:bg-slate-50">
                            <td className="p-2 font-mono text-xs">{order.keeta_order_id?.slice(0,12)}...</td>
                            <td className="p-2 font-bold">{order.keeta_order_no || "-"}</td>
                            <td className="p-2">{order.customer_name || "-"}</td>
                            <td className="p-2">R$ {(order.total || 0).toFixed(2)}</td>
                            <td className="p-2">
                              <Badge variant={
                                order.status === "CONFIRMED" ? "default" :
                                order.status === "PLACED" ? "destructive" :
                                order.status === "CONCLUDED" ? "secondary" : "outline"
                              }>
                                {order.status}
                              </Badge>
                            </td>
                            <td className="p-2">
                              {order.local_order_id ? 
                                <Badge variant="outline" className="text-green-600">Vinculado</Badge> : 
                                <Badge variant="outline" className="text-amber-600">Não Vinculado</Badge>
                              }
                            </td>
                            <td className="p-2 text-xs">{new Date(order.created_at).toLocaleString("pt-BR")}</td>
                            <td className="p-2">
                              {order.status === "PLACED" && (
                                <div className="flex gap-1">
                                  <Button size="sm" variant="outline" onClick={() => handleOrderAction(order.keeta_order_id, "confirm")}>
                                    <Check className="w-3 h-3" />
                                  </Button>
                                  <Button size="sm" variant="outline" onClick={() => handleOrderAction(order.keeta_order_id, "cancel")}>
                                    <X className="w-3 h-3" />
                                  </Button>
                                </div>
                              )}
                              {order.status === "CONFIRMED" && (
                                <Button size="sm" variant="outline" onClick={() => handleOrderAction(order.keeta_order_id, "ready")}>
                                  Pronto
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB: Loja */}
          <TabsContent value="store" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Store className="w-5 h-5" />Controle da Loja</CardTitle>
                <CardDescription>Abra ou pause sua loja no Keeta</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {loadingStore ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin" /></div>
                ) : (
                  <div className="space-y-4">
                    <div className="p-4 bg-slate-50 rounded-lg flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold">Status da Loja</h3>
                        <p className="text-sm text-muted-foreground">Controle se a loja está recebendo pedidos</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {storeInfo?.keeta_status && (
                          <span className="text-xs text-muted-foreground">Keeta: {storeInfo.keeta_status}</span>
                        )}
                        <Badge className={storeInfo?.is_open ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
                          {storeInfo?.is_open ? "Aberta" : "Fechada"}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <Button onClick={() => handleStoreToggle("open")} disabled={togglingStore || storeInfo?.is_open} className="flex-1">
                        {togglingStore ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-2" />}
                        Abrir Loja
                      </Button>
                      <Button onClick={() => handleStoreToggle("close")} variant="outline" disabled={togglingStore || !storeInfo?.is_open} className="flex-1">
                        {togglingStore ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PauseCircle className="w-4 h-4 mr-2" />}
                        Fechar Loja
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Horários de Funcionamento */}
            {storeInfo?.keeta_hours && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><Clock className="w-5 h-5" />Horarios de Funcionamento (Keeta)</CardTitle>
                  <CardDescription>Horarios configurados na plataforma Keeta (horario de Brasilia)</CardDescription>
                </CardHeader>
                <CardContent>
                  <KeetaStoreHours hours={storeInfo.keeta_hours} />
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* TAB: Menu */}
          <TabsContent value="menu" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Resumo do Menu Local */}
              <Card className="lg:col-span-2">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <UtensilsCrossed className="w-5 h-5" />
                      Cardápio Local
                    </CardTitle>
                    <CardDescription>Produtos disponíveis para sincronização</CardDescription>
                  </div>
                  <Button onClick={loadLocalMenu} variant="outline" size="sm" disabled={loadingMenu}>
                    <RefreshCw className={`w-4 h-4 ${loadingMenu ? "animate-spin" : ""}`} />
                  </Button>
                </CardHeader>
                <CardContent>
                  {loadingMenu ? (
                    <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin" /></div>
                  ) : (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 bg-slate-50 rounded-lg text-center">
                          <Package className="w-8 h-8 mx-auto text-blue-600" />
                          <p className="text-2xl font-bold mt-2">{localMenu.total_products || 0}</p>
                          <p className="text-sm text-muted-foreground">Produtos</p>
                        </div>
                        <div className="p-4 bg-slate-50 rounded-lg text-center">
                          <Menu className="w-8 h-8 mx-auto text-green-600" />
                          <p className="text-2xl font-bold mt-2">{localMenu.total_categories || 0}</p>
                          <p className="text-sm text-muted-foreground">Categorias</p>
                        </div>
                      </div>
                      
                      {/* Lista de Categorias */}
                      <div className="border rounded-lg divide-y max-h-[300px] overflow-y-auto">
                        {localMenu.categories?.map(cat => (
                          <div key={cat.id} className="p-3 flex justify-between items-center">
                            <div>
                              <p className="font-medium">{cat.name}</p>
                              <p className="text-xs text-muted-foreground">{cat.product_count} produtos</p>
                            </div>
                            <Badge variant="outline">{cat.product_count}</Badge>
                          </div>
                        ))}
                      </div>
                      
                      {/* Botão de Sync */}
                      <Button onClick={handleSyncMenu} disabled={syncing || !hasValidToken} className="w-full" size="lg">
                        {syncing ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sincronizando...</>
                        ) : (
                          <><Upload className="w-4 h-4 mr-2" />Enviar Cardápio para Keeta</>
                        )}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Histórico de Sincronizações */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Histórico de Sync</CardTitle>
                </CardHeader>
                <CardContent>
                  {syncHistory.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">Nenhuma sincronização realizada</p>
                  ) : (
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                      {syncHistory.map(sync => (
                        <div key={sync.id} className="p-3 bg-slate-50 rounded-lg text-sm">
                          <div className="flex justify-between items-start">
                            <Badge variant={sync.status === "COMPLETED" ? "default" : sync.status === "FAILED" ? "destructive" : "secondary"}>
                              {sync.status}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {new Date(sync.started_at).toLocaleString("pt-BR")}
                            </span>
                          </div>
                          <div className="mt-2 grid grid-cols-2 gap-1 text-xs">
                            <span>Categorias: {sync.categories_synced}</span>
                            <span>Produtos: {sync.products_synced}</span>
                            <span>Variações: {sync.variations_synced}</span>
                            <span className={sync.errors_count > 0 ? "text-red-600" : ""}>Erros: {sync.errors_count}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* TAB: Logs */}
          <TabsContent value="logs" className="space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Logs de API - Monitoramento</CardTitle>
                  <CardDescription>Visualize todas as requisições e respostas da API Keeta</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={logFilter} onValueChange={setLogFilter}>
                    <SelectTrigger className="w-[130px]">
                      <SelectValue placeholder="Filtrar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">Todos</SelectItem>
                      <SelectItem value="AUTH">Auth</SelectItem>
                      <SelectItem value="ORDER">Pedidos</SelectItem>
                      <SelectItem value="MENU">Menu</SelectItem>
                      <SelectItem value="STORE">Loja</SelectItem>
                      <SelectItem value="WEBHOOK">Webhook</SelectItem>
                      <SelectItem value="ERROR">Erros</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" onClick={loadLogs} disabled={logsLoading}>
                    <RefreshCw className={`w-4 h-4 ${logsLoading ? "animate-spin" : ""}`} />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {logsLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin" /></div>
                ) : logs.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">Nenhum log encontrado</p>
                ) : (
                  <div className="space-y-2">
                    {logs.map(log => (
                      <div 
                        key={log.id} 
                        className="p-3 border rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                        onClick={() => setSelectedLog(selectedLog?.id === log.id ? null : log)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Badge variant="outline" className="font-mono text-xs">
                              {log.log_type}
                            </Badge>
                            <span className="font-mono text-sm">
                              {log.method} {log.endpoint?.slice(0,40)}{log.endpoint?.length > 40 ? "..." : ""}
                            </span>
                            {getStatusBadge(log.status_code)}
                            <span className="text-xs text-muted-foreground">{log.response_time_ms}ms</span>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {new Date(log.created_at).toLocaleString("pt-BR")}
                          </span>
                        </div>
                        {log.error_message && (
                          <p className="text-xs text-red-600 mt-1">{log.error_message}</p>
                        )}
                        {selectedLog?.id === log.id && (
                          <div className="mt-3 p-3 bg-slate-100 rounded text-xs font-mono overflow-x-auto">
                            <p className="text-muted-foreground mb-1">Endpoint completo:</p>
                            <p className="mb-2">{log.endpoint}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB: Homologação */}
          <TabsContent value="homologacao" className="space-y-4">
            <KeetaHomologationTab />
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
}
